import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import Button from '../../../components/Button';
import { useState, useEffect } from 'react';
import TextInput from '../../../components/Inputs/Text';
import AmountInput from '../../../components/Inputs/Amount';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePrivy } from '@privy-io/react-auth';
import {
  useCreateExternalAccountMutation,
  useCreateOfframpTransferMutation,
  useGetExternalAccountsQuery,
  useGetLiquidationAddressesQuery,
  useCreateLiquidationAddressMutation,
  useCreatePlaidLinkTokenMutation,
  useExchangePlaidPublicTokenMutation,
} from '../../../redux/api/authorized/bridge';
import LoadingSpinner from '../../../components/LoadingSpinner';
import PlaidLink from '../../../components/PlaidLink';
import KycVerification from '../../../components/KycVerification';

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 900px;
    overflow-y: auto;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0px;
`;

const ConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0px;
`;

const ConfirmationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const ConfirmationLabel = styled(BodyLarge)`
  color: ${({ theme }) => theme.text.medium};
  flex-shrink: 0;
`;

const ConfirmationValue = styled(BodyLarge)`
  font-weight: 500;
  word-break: break-all;
  text-align: right;
`;

const AccountCard = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  background: ${({ theme }) => theme.background.primary};

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) => theme.primary.accent};
      cursor: pointer;
    }
  }

  &.selected {
    border-color: ${({ theme }) => theme.primary.accent};
    background: ${({ theme }) => theme.primary.background};
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AccountTitle = styled(BodyLarge)`
  font-weight: 600;
`;

const AccountDetail = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.text.medium};
`;

const LiquidationAddress = ({ liquidationAddress, liquidationData, index }) => {
  const externalAccount = liquidationData?.external_accounts?.find(
    (acc) => acc.id === liquidationAddress?.external_account_id,
  );

  const theme = useTheme();

  return (
    <div
      key={liquidationAddress.id || index}
      style={{
        marginBottom: '24px',
        border: `1px solid ${theme.border.primary}`,
        borderRadius: '8px',
        padding: '16px',
      }}
    >
      <BodyLarge style={{ fontWeight: 'bold', marginBottom: '12px' }}>
        Address {index + 1}
      </BodyLarge>

      <ConfirmationRow>
        <ConfirmationLabel>Network</ConfirmationLabel>
        <ConfirmationValue>
          {liquidationAddress.chain.toUpperCase()}
        </ConfirmationValue>
      </ConfirmationRow>

      <ConfirmationRow>
        <ConfirmationLabel>Currency</ConfirmationLabel>
        <ConfirmationValue>
          {liquidationAddress.currency.toUpperCase()}
        </ConfirmationValue>
      </ConfirmationRow>

      <ConfirmationRow>
        <ConfirmationLabel>Liquidation Address</ConfirmationLabel>
        <ConfirmationValue
          style={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            fontSize: '12px',
          }}
        >
          {liquidationAddress.address}
        </ConfirmationValue>
      </ConfirmationRow>

      {/* Show associated bank account info */}
      {externalAccount && (
        <ConfirmationRow>
          <ConfirmationLabel>Bank Account</ConfirmationLabel>
          <ConfirmationValue>
            {externalAccount.account_owner_name}
            <br />
            {externalAccount.account_details && (
              <>
                {externalAccount.account_details?.account_type} ****
                {externalAccount.account_details?.account_number?.slice(-4)}
              </>
            )}
            {!externalAccount.account_details && (
              <>
                {externalAccount.account_type} ****{externalAccount.last_4}
              </>
            )}
          </ConfirmationValue>
        </ConfirmationRow>
      )}
    </div>
  );
};

const OfframpTransferFlow = ({ bridgeCustomerId, onClose, amount }) => {
  const theme = useTheme();
  const { user } = usePrivy();
  const [createExternalAccount] = useCreateExternalAccountMutation();
  const [createLiquidationAddress] = useCreateLiquidationAddressMutation();
  const [createOfframpTransfer] = useCreateOfframpTransferMutation();

  const [flowState, setFlowState] = useState('connecting'); // 'connecting' | 'success'
  const [liquidationData, setLiquidationData] = useState(null);
  const [selectedExternalAccount, setSelectedExternalAccount] = useState(null);
  const [connectionMethod, setConnectionMethod] = useState(null); // 'plaid' only now
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [error, setError] = useState(null);

  // Get existing external accounts and liquidation addresses
  const { data: externalAccountsData, isLoading: externalAccountsLoading } =
    useGetExternalAccountsQuery(undefined, {
      skip: !user || !bridgeCustomerId,
    });

  const {
    data: liquidationAddressesData,
    isLoading: liquidationAddressesLoading,
  } = useGetLiquidationAddressesQuery(undefined, {
    skip: !user || !bridgeCustomerId,
  });

  const externalAccounts = externalAccountsData?.data;
  const liquidationAddresses = liquidationAddressesData?.data;

  // Check for existing accounts when component mounts
  useEffect(() => {
    if (
      bridgeCustomerId &&
      !externalAccountsLoading &&
      !liquidationAddressesLoading
    ) {
      if (externalAccounts && externalAccounts.length > 0) {
        // User has existing external accounts - check for liquidation addresses
        if (liquidationAddresses && liquidationAddresses.length > 0) {
          // User has both - show existing liquidation addresses
          setLiquidationData({
            external_accounts: externalAccounts,
            liquidation_addresses: liquidationAddresses,
          });
          setFlowState('success');
        } else {
          // User has external accounts but no liquidation addresses
          // Show external account selection for creating liquidation address
          // For now, auto-select first account and create liquidation address
          setSelectedExternalAccount(externalAccounts[0]);
          handleCreateLiquidationAddress(externalAccounts[0].id);
        }
      } else {
        // No existing accounts - automatically start Plaid Link
        setConnectionMethod('plaid');
      }
    }
  }, [
    bridgeCustomerId,
    externalAccounts,
    externalAccountsLoading,
    liquidationAddresses,
    liquidationAddressesLoading,
  ]);

  const handleCreateLiquidationAddress = async (externalAccountId) => {
    try {
      setLoading(true);

      const response = await createLiquidationAddress({
        external_account_id: externalAccountId,
        chain: 'solana',
        currency: 'usdc',
      }).unwrap();

      setLiquidationData(response);
      setFlowState('success');
      setLoading(false);
    } catch (error) {
      // Error creating liquidation address
      setLoading(false);
    }
  };

  const handlePlaidSuccess = (data) => {
    // Plaid has successfully connected and Bridge created accounts
    setLiquidationData(data);
    setFlowState('success');
  };

  const handlePlaidError = (error) => {
    console.error('Plaid error:', error);
    // Stay in connecting state, let user try again
  };

  const handleStartOver = () => {
    setFlowState('connecting');
    setLiquidationData(null);
    setSelectedExternalAccount(null);
    setConnectionMethod('plaid');
  };

  return (
    <ContentContainer>
      {flowState === 'connecting' && (
        <>
          <ParagraphLargeText>Connect Bank Account</ParagraphLargeText>

          {(externalAccountsLoading || liquidationAddressesLoading) && (
            <>
              <BodyLarge>Checking existing accounts...</BodyLarge>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '20px 0',
                }}
              >
                <LoadingSpinner />
              </div>
            </>
          )}

          {!externalAccountsLoading &&
            !liquidationAddressesLoading &&
            connectionMethod === 'plaid' && (
              <>
                <BodyLarge>
                  Connect your bank account instantly with Plaid to set up
                  automatic USDC cashouts.
                </BodyLarge>

                <PlaidLink
                  onSuccess={handlePlaidSuccess}
                  onError={handlePlaidError}
                />
              </>
            )}
        </>
      )}

      {flowState === 'success' && liquidationData && (
        <ConfirmationContainer>
          <ParagraphLargeText>
            Your Liquidation Address
            {liquidationData.liquidation_addresses?.length > 1 ? 'es' : ''}
          </ParagraphLargeText>
          <BodyLarge>
            Send USDC to{' '}
            {liquidationData.liquidation_addresses?.length > 1
              ? 'any of these addresses'
              : 'this address'}{' '}
            anytime to automatically convert to USD in your bank account:
          </BodyLarge>

          <BodyLarge
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: `${theme.primary.background}`,
              border: `1px solid ${theme.primary.accent}`,
              borderRadius: '8px',
              fontSize: '14px',
            }}
          >
            <strong>Important:</strong> Save{' '}
            {liquidationData.liquidation_addresses?.length > 1
              ? 'these addresses'
              : 'this address'}
            ! You can use{' '}
            {liquidationData.liquidation_addresses?.length > 1 ? 'them' : 'it'}{' '}
            repeatedly to cash out USDC to your bank account.
          </BodyLarge>

          {/* Handle multiple existing liquidation addresses */}
          {liquidationData.liquidation_addresses &&
            liquidationData.liquidation_addresses.map(
              (liquidationAddress, index) => (
                <LiquidationAddress
                  key={index}
                  liquidationAddress={liquidationAddress}
                  liquidationData={liquidationData}
                  index={index}
                />
              ),
            )}

          {/* Handle single newly created liquidation address */}
          {liquidationData.liquidation_address && (
            <>
              <ConfirmationRow>
                <ConfirmationLabel>Network</ConfirmationLabel>
                <ConfirmationValue>
                  {liquidationData.liquidation_address.chain.toUpperCase()}
                </ConfirmationValue>
              </ConfirmationRow>

              <ConfirmationRow>
                <ConfirmationLabel>Currency</ConfirmationLabel>
                <ConfirmationValue>
                  {liquidationData.liquidation_address.currency.toUpperCase()}
                </ConfirmationValue>
              </ConfirmationRow>

              <ConfirmationRow>
                <ConfirmationLabel>Liquidation Address</ConfirmationLabel>
                <ConfirmationValue
                  style={{
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    fontSize: '12px',
                  }}
                >
                  {liquidationData.liquidation_address.address}
                </ConfirmationValue>
              </ConfirmationRow>

              <ConfirmationRow>
                <ConfirmationLabel>Bank Account</ConfirmationLabel>
                <ConfirmationValue>
                  {liquidationData.external_account.account_owner_name}
                  <br />
                  {
                    liquidationData.external_account.account_details
                      .account_type
                  }{' '}
                  ****
                  {liquidationData.external_account.account_details.account_number.slice(
                    -4,
                  )}
                </ConfirmationValue>
              </ConfirmationRow>
            </>
          )}

          <Button type='button' fullWidth={true} onClick={onClose}>
            Close
          </Button>

          <Button
            type='button'
            variant='secondary'
            fullWidth={true}
            onClick={handleStartOver}
          >
            Create Another Address
          </Button>
        </ConfirmationContainer>
      )}
    </ContentContainer>
  );
};

const BridgeOfframpModal = ({ onClose, amount }) => {
  return (
    <KycVerification onClose={onClose}>
      {({ bridgeCustomerId }) => (
        <OfframpTransferFlow
          bridgeCustomerId={bridgeCustomerId}
          onClose={onClose}
          amount={amount}
        />
      )}
    </KycVerification>
  );
};

export default BridgeOfframpModal;
